import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { Box, Button, Flex, Spinner, Text, useColorMode } from 'theme-ui';
import * as Yup from 'yup';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import moment from 'moment';
import { Inputform } from '../../../forms-component/InputForm';
import FormSelect from '../../../forms-component/globalSelect';
import { uploadMedia } from '../../../../store/api';
import deleteIcon from '../../../assets/images/Icon/delete.svg';
import { ContractModalProps, FormValues, ISalesRepObject } from '../../../../../@types';



function isSalesRepObject(obj: unknown): obj is ISalesRepObject {
  return typeof obj === 'object' && obj !== null && '_id' in obj;
}

export function ContractModal({ isOpen, toggle, onAdd, salesReps, contract }: ContractModalProps) {
  const [colorMode] = useColorMode();
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>('');

  const modalBg = colorMode === 'light' ? '#fff' : '#20283c';
  const textColor = colorMode === 'light' ? '#0a0a0a' : '#fff';
  const labelColor = colorMode === 'light' ? 'secondary' : 'white';

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      proposalName: contract?.proposalName || '',
      dateOfApproval: contract?.dateOfApproval
        ? moment(contract.dateOfApproval).format('YYYY-MM-DD')
        : '',
      salesRepresentative: ((): string => {
        const rep: unknown = contract?.salesRepresentative;
        if (!rep) return '';
        if (isSalesRepObject(rep)) {
          return rep._id || '';
        }
        if (typeof rep === 'string') {
          return rep;
        }
        return '';
      })(),
      attachments: contract?.attachments || [],
    },
    validationSchema: Yup.object().shape({
      proposalName: Yup.string().required('Proposal Name is required'),
      dateOfApproval: Yup.string().required('Date of Approval is required'),
      salesRepresentative: Yup.string().required('Sales Representative is required'),
      attachments: Yup.array().of(Yup.string()),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await onAdd({
          ...contract,
          proposalName: values.proposalName,
          dateOfApproval: values.dateOfApproval,
          salesRepresentative: values.salesRepresentative,
          attachments: values.attachments,
        });
        formik.resetForm();
        toggle();
      } catch (err) {
        console.error('Error during contract submit:', err);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { handleSubmit, setFieldValue, handleBlur, touched, values, errors } = formik;

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    setIsUploading(true);
    setUploadError('');

    try {
      const uploadedPaths: string[] = [...values.attachments];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const extension = file.name.split('.').pop()?.toLowerCase();
        const allowedExtensions = ['pdf', 'png', 'jpg', 'jpeg', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'];
        if (!extension || !allowedExtensions.includes(extension)) {
          setUploadError('Unsupported file format. Allowed: PDF, PNG, JPG, JPEG, DOC, DOCX, XLS, XLSX, PPT, PPTX');
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'contract');
        formData.append('uploadType', 'File');

        const response = await uploadMedia(formData);
        if (response?.data?.status && response?.data?.data) {
          uploadedPaths.push(response.data.data);
        } else {
          setUploadError('Failed to upload file');
        }
      }
      setFieldValue('attachments', uploadedPaths, true);
    } catch (err) {
      console.error('File upload error:', err);
      setUploadError('An error occurred during file upload');
    } finally {
      setIsUploading(false);
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const removeAttachment = (indexToRemove: number) => {
    const updated = values.attachments.filter((_, idx) => idx !== indexToRemove);
    setFieldValue('attachments', updated, true);
  };

  const getFileName = (path: string): string => {
    return path.split('/').pop() || path;
  };

  const handleToggle = () => {
    if (isUploading || formik.isSubmitting) {
      return;
    }
    formik.resetForm();
    toggle();
  };

  useEffect(() => {
    if (isOpen && !contract) {
      formik.resetForm();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleToggle}
      backdrop={isUploading || formik.isSubmitting ? 'static' : true}
      keyboard={!(isUploading || formik.isSubmitting)}
      centered
      style={{ maxWidth: '700px', width: '100%' }}
    >
      <ModalHeader
        toggle={handleToggle}
        style={{
          backgroundColor: modalBg,
          color: textColor,
          borderBottom: '1px solid #eaeaea',
        }}
      >
        <span style={{ fontSize: '18px', fontWeight: 600 }}>{contract ? 'EDIT CONTRACT' : 'ADD NEW CONTRACT'}</span>
      </ModalHeader>
      <ModalBody style={{ backgroundColor: modalBg, color: textColor, padding: '24px' }}>
        <form onSubmit={handleSubmit}>
          <Box mb={3}>
            <Inputform
              label="Proposal Name"
              Placeholder="Enter Proposal Name..."
              Name="proposalName"
              type="text"
              sx={{ color: labelColor }}
              touched={touched.proposalName}
              errors={errors.proposalName}
              values={values.proposalName}
              asterisk={true}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('proposalName', e.target.value, true);
              }}
            />
          </Box>

          <Box mb={3}>
            <Inputform
              inputClassName="custom-datepicker-input"
              wrapperClassName="date-input-wrapper"
              type="date"
              label="Date of Approval"
              Placeholder="mm-dd-yyyy"
              dateFormat="MM-dd-yyyy"
              Name="dateOfApproval"
              sx={{ color: labelColor }}
              touched={touched.dateOfApproval}
              errors={errors.dateOfApproval}
              values={values.dateOfApproval}
              asterisk={true}
              onBlur={handleBlur}
              OnChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setFieldValue('dateOfApproval', e.target.value, true);
              }}
            />
          </Box>

          <Box mb={3}>
            <FormSelect
              label="Sales Representative"
              value={values.salesRepresentative}
              placeholder="Select Sales Representative"
              options={salesReps.map((rep) => ({
                label: `${rep.FirstName} ${rep.LastName}`,
                value: rep._id,
              }))}
              isMulti={false}
              sx={{ color: labelColor }}
              touched={touched.salesRepresentative}
              errors={errors.salesRepresentative}
              onBlur={handleBlur}
              Name="salesRepresentative"
              asterisk={true}
              onChange={(option: { label: string; value: string } | null) => {
                setFieldValue('salesRepresentative', option ? option.value : '', true);
              }}
            />
          </Box>

          <Box mb={3}>
            <Text variant="SelectLabel" sx={{ color: labelColor }}>
              Signed Contract
            </Text>
            <input
              type="file"
              multiple
              disabled={isUploading}
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
              style={{ display: 'none' }}
              id="contract-file-upload"
              onChange={handleFileChange}
            />
            <label htmlFor={isUploading ? undefined : 'contract-file-upload'} style={{ display: 'block', marginTop: '8px' }}>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: '#D3D5D9',
                  borderRadius: '10px',
                  padding: '24px',
                  textAlign: 'center',
                  cursor: isUploading ? 'not-allowed' : 'pointer',
                  backgroundColor: colorMode === 'light' ? '#fcfcfc' : '#1e1b2f',
                  '&:hover': {
                    borderColor: isUploading ? '#D3D5D9' : '#7743DB',
                  },
                }}
              >
                {isUploading ? (
                  <Flex sx={{ justifyContent: 'center', alignItems: 'center', gap: 2 }}>
                    <Spinner size={20} />
                    <Text sx={{ fontSize: '14px', fontWeight: 500 }}>Uploading files...</Text>
                  </Flex>
                ) : (
                  <Text sx={{ fontSize: '14px', color: '#6c757d' }}>
                    Click or drag files here to upload
                  </Text>
                )}
              </Box>
            </label>

            {uploadError && (
              <Text sx={{ color: 'red', fontSize: '12px', mt: 1, display: 'block' }}>
                {uploadError}
              </Text>
            )}



            {values.attachments.length > 0 && (
              <Box mt={3}>
                <Text sx={{ fontSize: '14px', fontWeight: 600, mb: 2, display: 'block' }}>
                  Uploaded Files:
                </Text>
                <Flex sx={{ flexDirection: 'column', gap: 2 }}>
                  {values.attachments.map((file, idx) => (
                    <Flex
                      key={idx}
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 12px',
                        border: '1px solid #eaeaea',
                        borderRadius: '8px',
                        backgroundColor: colorMode === 'light' ? '#f8f9fa' : '#282b3d',
                      }}
                    >
                      <Text
                        sx={{
                          fontSize: '13px',
                          color: colorMode === 'light' ? '#495057' : 'white',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '80%',
                        }}
                      >
                        {getFileName(file)}
                      </Text>
                      <Box
                        sx={{ cursor: 'pointer' }}
                        onClick={() => removeAttachment(idx)}
                      >
                        <img src={deleteIcon} alt="Delete file" width="16" height="16" />
                      </Box>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            )}
          </Box>

          <Flex sx={{ justifyContent: 'flex-end', gap: 2, mt: 4 }}>
            <Button
              type="button"
              disabled={isUploading || formik.isSubmitting}
              onClick={handleToggle}
              sx={{
                backgroundColor: 'white',
                color: '#7743DB',
                border: '1px solid #7743DB',
                borderRadius: '10px',
                padding: '10px 20px',
                fontWeight: 500,
                cursor: (isUploading || formik.isSubmitting) ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUploading || formik.isSubmitting}
              sx={{
                backgroundColor: '#7743DB',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '10px 20px',
                fontWeight: 500,
                cursor: (isUploading || formik.isSubmitting) ? 'not-allowed' : 'pointer',
              }}
            >
              {formik.isSubmitting ? <Spinner size={20} sx={{ color: '#fff' }} /> : 'Save'}
            </Button>
          </Flex>
        </form>
      </ModalBody>
    </Modal>
  );
}
